/**
 * "Ask to allow": a machine asked its owner to allow screen or terminal access from the console and the phone. One
 * calm row in what the machine allows: "Waiting: asked 5 min ago for screen control and typing in terminals", with
 * Allow and Not now right there. The same ask is in the owner's Inbox; either place decides it. Nothing shows when
 * the machine is not waiting, or the person may not see what it asked.
 */

import { scopeRequestWords } from '@aioe/shared';
import { useState } from 'react';

import { ApiError, routes, SIGN_IN_AGAIN } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, ErrorNote } from './ui.tsx';

export function ScopeRequestWaiting({ deviceId, onDecided }: { deviceId: string; onDecided?: () => void }) {
  const ask = useLoad(() => routes.scopeRequest(deviceId), [deviceId]);
  const { signInAgain } = useAuth();
  const [busy, setBusy] = useState<'allow' | 'refuse'>();
  const [error, setError] = useState<string>();
  const r = ask.data;
  if (!r || r.state !== 'open') return null;

  async function decide(decision: 'allow' | 'refuse', retried = false): Promise<void> {
    setBusy(decision);
    setError(undefined);
    try {
      await routes.decideScopeRequest(deviceId, r!.requestId, decision);
      ask.reload();
      onDecided?.();
    } catch (e) {
      // Allowing screen control or typing needs a recent sign-in: ask for one, then try once more.
      if (e instanceof ApiError && e.code === SIGN_IN_AGAIN && signInAgain && !retried) {
        try {
          await signInAgain(e.message);
        } catch {
          setError('Allowing this needs a recent sign-in. Choose Allow again when you are ready to sign in.');
          return;
        }
        return decide(decision, true);
      }
      setError((e as Error).message);
      ask.reload();
    } finally {
      setBusy(undefined);
    }
  }

  return (
    // One more row above the allow rows, with their divider under it.
    <div className="grid gap-2 mb-3 border-b border-separator pb-3">
      {/* Narrow: the words take the whole width and the two buttons go under them. */}
      <div className="grid gap-2 sm:flex sm:items-center sm:justify-between sm:gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">
            Waiting: asked {timeAgo(r.requestedAt ?? r.expiresAt)} for {scopeRequestWords(r.scopes)}
          </span>
          <span className="block text-xs text-muted">{r.note ? `It says: “${r.note}”. ` : ''}Allow adds this to what the machine allows. The ask lapses after a day.</span>
        </span>
        <span className="flex shrink-0 justify-end gap-2">
          <Button size="sm" variant="ghost" isDisabled={Boolean(busy)} onPress={() => void decide('refuse')}>
            Not now
          </Button>
          <Button size="sm" variant="primary" isDisabled={Boolean(busy)} onPress={() => void decide('allow')}>
            Allow
          </Button>
        </span>
      </div>
      <ErrorNote error={error} />
    </div>
  );
}
